import { cn } from '@massilia/ui'
import {
  addMonths,
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
  startOfMonth,
  startOfToday,
} from 'date-fns'
import { fr } from 'date-fns/locale'

import { centsToEuros, formatPrice } from '@/utils/format-price'

interface InstallmentTimelineProps {
  balanceDeadline: string
  departureDate: string
  depositDeadline?: string
  minimumDepositCents?: number
}

const DEPOSIT_PILL_FALLBACK_PERCENT = 5
const DEPOSIT_HINT_TEXT = 'La date limite de versement sera précisée après réservation'

export function InstallmentTimeline({
  balanceDeadline,
  departureDate,
  depositDeadline,
  minimumDepositCents,
}: InstallmentTimelineProps) {
  const today = startOfToday()
  const departure = parseDate(departureDate)
  const balance = parseDate(balanceDeadline)
  const deposit = depositDeadline ? parseDate(depositDeadline) : null
  const hasDepositInfo = minimumDepositCents != null
  const hasDepositDate = deposit != null && minimumDepositCents != null

  if (!departure || !balance) return null

  const totalSpanDays = differenceInCalendarDays(departure, today)
  if (totalSpanDays <= 0) return null

  const toPercent = (date: Date): number => {
    const days = differenceInCalendarDays(date, today)
    return Math.max(0, Math.min(100, (days / totalSpanDays) * 100))
  }

  const monthlyTickDates = collectMonthlyTickDates(today, balance, deposit)
  const balanceToDepartureDays = differenceInCalendarDays(departure, balance)
  const depositPercent = deposit ? toPercent(deposit) : DEPOSIT_PILL_FALLBACK_PERCENT
  const balancePercent = toPercent(balance)

  const depositLabel = hasDepositDate
    ? `Acompte minimum de ${formatPrice(centsToEuros(minimumDepositCents))} avant le ${formatLabelDate(deposit)}`
    : hasDepositInfo
      ? `Montant minimum de l'acompte : ${formatPrice(centsToEuros(minimumDepositCents))}`
      : null
  const balanceLabel = `Solde au plus tard le ${formatLabelDate(balance)}`

  return (
    <div className="mt-4 w-full">
      <p className="text-gray-light-900 text-sm font-semibold">Échéancier de paiement</p>

      {/* Mobile : résumé textuel, à même la page — pas de carte ni d'indentation */}
      <div className="mt-2 flex flex-col gap-1.5 sm:hidden">
        {hasDepositInfo && (
          <div>
            <p className="text-gray-light-600 text-sm leading-relaxed">
              Acompte minimum de <Em>{formatPrice(centsToEuros(minimumDepositCents))}</Em> à
              verser
              {hasDepositDate ? (
                <>
                  <br />
                  avant le <Em>{formatLabelDate(deposit)}</Em>.
                </>
              ) : (
                '.'
              )}
            </p>
            {!hasDepositDate && (
              <p className="text-gray-light-500 mt-0.5 text-xs italic">{DEPOSIT_HINT_TEXT}</p>
            )}
          </div>
        )}
        <p className="text-gray-light-600 text-sm leading-relaxed">
          Puis un paiement par mois minimum, à votre rythme.
        </p>
        <p className="text-gray-light-600 text-sm leading-relaxed">
          Dernier règlement au plus tard{' '}
          <Em>
            {balanceToDepartureDays} jour{balanceToDepartureDays > 1 ? 's' : ''}
          </Em>{' '}
          avant votre départ : le <Em>{formatLabelDate(balance)}</Em>.
        </p>
      </div>

      {/* Desktop : timeline graphique, sans carte */}
      <div className="mt-3 hidden sm:block">
        {/* Floating deposit label above the bar */}
        {depositLabel && (
          <div className="relative h-12">
            <FloatingLabel
              align="bottom"
              hint={hasDepositDate ? undefined : DEPOSIT_HINT_TEXT}
              leftPercent={depositPercent}
              showConnector={hasDepositDate}
              text={depositLabel}
            />
          </div>
        )}

        {/* Bar with ticks */}
        <div className="relative h-4 w-full">
          <div className="bg-gray-light-300 absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full" />
          <Endpoint leftPercent={0} />
          <Endpoint leftPercent={100} />
          {monthlyTickDates.map((date) => (
            <Tick key={date.toISOString()} leftPercent={toPercent(date)} />
          ))}
          {hasDepositDate && <Tick highlighted leftPercent={depositPercent} />}
          <Tick highlighted leftPercent={balancePercent} />
        </div>

        {/* Endpoint labels */}
        <div className="text-gray-light-500 mt-2 flex justify-between text-xs font-medium">
          <span>Aujourd&apos;hui</span>
          <span>Départ le {format(departure, 'd MMMM yyyy', { locale: fr })}</span>
        </div>

        {/* Floating balance label below the bar */}
        <div className="relative mt-2 h-12">
          <FloatingLabel align="top" leftPercent={balancePercent} text={balanceLabel} />
        </div>

        <p className="text-gray-light-600 mt-1 text-center text-xs font-medium">
          1 paiement par mois minimum
        </p>
      </div>
    </div>
  )
}

function collectMonthlyTickDates(today: Date, balance: Date, deposit: Date | null): Date[] {
  const ticks: Date[] = []
  let cursor = startOfMonth(addMonths(today, 1))
  while (cursor.getTime() < balance.getTime()) {
    if (!deposit || !isSameDay(cursor, deposit)) ticks.push(cursor)
    cursor = addMonths(cursor, 1)
  }
  return ticks
}

function Em({ children }: { children: React.ReactNode }) {
  return <span className="text-brand-700 font-semibold">{children}</span>
}

function Endpoint({ leftPercent }: { leftPercent: number }) {
  return (
    <div
      className="bg-brand-600 absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{ left: `${leftPercent}%` }}
    />
  )
}

function FloatingLabel({
  align,
  hint,
  leftPercent,
  showConnector = true,
  text,
}: {
  align: 'bottom' | 'top'
  hint?: string
  leftPercent: number
  showConnector?: boolean
  text: string
}) {
  const clamped = Math.max(0, Math.min(100, leftPercent))
  const anchorRight = clamped > 70
  const anchorLeft = clamped < 30

  const labelStyle: React.CSSProperties = anchorRight
    ? { right: `${100 - clamped}%` }
    : anchorLeft
      ? { left: `${clamped}%` }
      : { left: `${clamped}%`, transform: 'translateX(-50%)' }

  return (
    <>
      <div
        className={cn('absolute flex flex-col gap-0.5', align === 'top' ? 'top-2' : 'bottom-2')}
        style={labelStyle}
      >
        <span className="border-brand-100 bg-brand-50 text-gray-light-900 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap shadow-sm">
          {text}
        </span>
        {hint && (
          <span className="text-gray-light-500 text-[11px] whitespace-nowrap italic">{hint}</span>
        )}
      </div>
      {showConnector && (
        <span
          className={cn('bg-brand-300 absolute h-2 w-px', align === 'top' ? 'top-0' : 'bottom-0')}
          style={{ left: `${clamped}%`, transform: 'translateX(-50%)' }}
        />
      )}
    </>
  )
}

function formatLabelDate(date: Date): string {
  return format(date, 'd MMMM yyyy', { locale: fr })
}

function parseDate(value: string): Date | null {
  try {
    const parsed = value.includes('T') ? parseISO(value) : parseISO(`${value}T00:00:00`)
    return isNaN(parsed.getTime()) ? null : parsed
  } catch {
    return null
  }
}

function Tick({
  highlighted = false,
  leftPercent,
}: {
  highlighted?: boolean
  leftPercent: number
}) {
  return (
    <div
      className={cn(
        'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-sm',
        highlighted ? 'bg-brand-600 h-3.5 w-0.5' : 'bg-gray-light-500 h-2 w-px'
      )}
      style={{ left: `${leftPercent}%` }}
    />
  )
}
